import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeIngestWindow } from '@repo/shared-types';
import {
  AlertTriangle, Bot, ChevronDown, ChevronRight, Clock, FileText, FolderTree, Hash, Inbox, Loader2,
  Mail, MessageSquareText, Mic, Paperclip, Pencil, Play, RefreshCw, Scissors, Terminal, Upload,
} from 'lucide-react';
import { api, type FeedSourceType, type IngestQueueProject, type WaitingSegment } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { formatSource, useSourceLabels } from '../../lib/source-label';
import { formatDate, timeAgo } from '../../lib/utils';

/** Icon per arrival kind, matching the project overview's activity list. */
const SOURCE_ICONS: Record<FeedSourceType, typeof Mic> = {
  GRANOLA: Mic,
  SLACK: Hash,
  FILE_UPLOAD: Upload,
  CLI_IMPORT: Terminal,
  MANUAL: FileText,
  MCP: Bot,
  USER_EDIT: Pencil,
  USER_FEEDBACK: MessageSquareText,
  // A mail attachment and a Slack-linked file are both LINKED_DOCUMENT: a paperclip reads as
  // "came with something else", which is exactly what distinguishes it from a deliberate upload.
  LINKED_DOCUMENT: Paperclip,
  EMAIL: Mail,
  DRIVE_FILE: FileText,
  // A person's correction to an automation's document — a pencil, like any other hand edit.
  AUTOMATION_EDIT: Pencil,
};

/**
 * What has arrived but not yet been absorbed, and when it will be.
 *
 * This page exists because batching made "it arrived" and "it was absorbed" two different moments for
 * the first time. Until now every arrival produced a run within seconds, so the Runs tab answered both
 * questions at once; a project with a window has material that is captured, filed, and deliberately
 * sitting still — which without this looks exactly like material that was dropped.
 *
 * Org-level, like everything else on this page: batching is opted into per project, so "is anything
 * backed up" is a question about the tree rather than about one project.
 */
export function IngestQueue() {
  const [projects, setProjects] = useState<IngestQueueProject[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Whether that notice was about work actually starting, which is what makes the link worth showing. */
  const [startedRuns, setStartedRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Bumped whenever the queue may have moved, and passed down to any open item list.
   *
   * The counts and the items are two requests, so without this a row expanded before pressing Process
   * now keeps listing pieces a run has already claimed — the aggregate above it corrects itself and the
   * list under it silently doesn't.
   */
  const [version, setVersion] = useState(0);

  const load = useCallback(
    () =>
      api.kb
        .ingestQueue()
        .then((page) => setProjects(page.projects))
        .catch(() => setProjects([])),
    [],
  );

  const refresh = useCallback(async () => {
    await load();
    setVersion((v) => v + 1);
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  /** `pool` narrows it to that pool's next run alone — one batch at a time rather than the whole queue. */
  const process = async (projectId?: string, pool?: 'place' | 'pinned') => {
    setBusy(projectId ? (pool ? `${projectId}:${pool}` : projectId) : 'all');
    setError(null);
    setNotice(null);
    try {
      const result = await api.kb.processIngestQueue(projectId, pool ? { pool, batches: 1 } : {});
      setStartedRuns(result.runs > 0);
      setNotice(
        result.runs === 0
          ? 'Nothing was waiting.'
          : `Started ${result.runs} run${result.runs === 1 ? '' : 's'} over ${result.segments} item${result.segments === 1 ? '' : 's'}.`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not process the queue');
    } finally {
      setBusy(null);
    }
  };

  const waiting = (projects ?? []).filter((project) => project.segments > 0);
  const totalItems = waiting.reduce((sum, project) => sum + project.segments, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg border border-border bg-surface-subtle px-4 py-3">
        <Inbox size={15} className="text-orange-500 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-ink-muted leading-relaxed space-y-1">
          <p>
            A project can absorb arrivals as they land, or hold them for a window once a day. Holding
            them lets the agent read a whole day's material in one pass, in the order things happened,
            instead of re-reading the knowledge base once per message.
          </p>
          <p>
            Only automatic sources wait. A pasted document, an upload and a note saved by an automation
            are always absorbed at once. Set a project's window on its own{' '}
            <span className="font-medium text-ink-secondary">Settings → Integrations</span> page.
          </p>
        </div>
      </div>

      {notice && (
        <p className="text-xs text-success-text bg-success-bg border border-success-ring rounded px-3 py-2">
          {notice}
          {/* The runs start immediately and take a minute of model time, so the useful next move is to
              go and watch them — without this the page says work began and then points nowhere. */}
          {startedRuns && (
            <>
              {' '}
              <Link to="/kb-review?tab=runs" className="underline hover:text-ink">
                Watch them run
              </Link>
              .
            </>
          )}
        </p>
      )}
      {error && (
        <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
      )}

      <Card>
        <CardHeader className="flex items-center gap-2">
          <CardTitle>Waiting to be absorbed</CardTitle>
          {totalItems > 0 && (
            <span className="text-[11px] text-ink-faint">
              {totalItems} item{totalItems === 1 ? '' : 's'} across {waiting.length} project
              {waiting.length === 1 ? '' : 's'}
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            {totalItems > 0 && (
              <Button variant="secondary" onClick={() => process()} disabled={busy !== null}>
                {busy === 'all' ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                Process everything
              </Button>
            )}
            <button
              onClick={() => void refresh()}
              className="text-ink-faint hover:text-ink transition-colors"
              title="Refresh"
            >
              <RefreshCw size={13} />
            </button>
          </div>
        </CardHeader>

        <CardContent>
          {projects === null ? (
            <p className="flex items-center gap-2 text-sm text-ink-faint py-4">
              <Loader2 size={14} className="animate-spin" />
              Loading…
            </p>
          ) : projects.length === 0 ? (
            <p className="text-sm text-ink-faint py-4 text-center">
              Nothing is waiting, and no project has a window set — every arrival is absorbed as it lands.
            </p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {projects.map((project) => (
                <QueueRow
                  key={project.projectId}
                  project={project}
                  busy={busy === project.projectId}
                  busyPool={
                    busy === `${project.projectId}:place` ? 'place'
                      : busy === `${project.projectId}:pinned` ? 'pinned'
                        : null
                  }
                  disabled={busy !== null}
                  version={version}
                  onProcess={() => process(project.projectId)}
                  onProcessNext={(pool) => process(project.projectId, pool)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QueueRow({
  project,
  busy,
  busyPool,
  disabled,
  version,
  onProcess,
  onProcessNext,
}: {
  project: IngestQueueProject;
  busy: boolean;
  /** Which pool's next run is being dispatched, if that is what is in flight. */
  busyPool: 'place' | 'pinned' | null;
  disabled: boolean;
  /** Changes when the queue may have moved, so an open list refetches rather than going stale. */
  version: number;
  onProcess: () => void;
  onProcessNext: (pool: 'place' | 'pinned') => void;
}) {
  const scheduled = project.mode === 'SCHEDULED';
  const [open, setOpen] = useState(false);

  return (
    <li className="py-3 first:pt-0 last:pb-0">
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Only where there is something to open. A disclosure on an empty queue would promise a
              list and then show "nothing waiting" twice. */}
          {project.segments > 0 ? (
            <button
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="text-ink-faint hover:text-ink transition-colors -ml-0.5"
              title={open ? 'Hide what is waiting' : 'See what is waiting'}
            >
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : (
            <span className="w-[13px]" />
          )}
          <Link
            to={`/p/${project.projectSlug}/kb`}
            className="text-sm font-medium text-ink-secondary hover:text-orange-500 transition-colors"
          >
            {project.projectName}
          </Link>
          {project.segments > 0 ? (
            <button
              onClick={() => setOpen((o) => !o)}
              className="text-[11px] px-2 py-0.5 rounded border border-border bg-surface-subtle text-ink-muted hover:border-orange-300 hover:text-ink transition-colors"
            >
              {project.segments} item{project.segments === 1 ? '' : 's'} · {formatChars(project.chars)}
            </button>
          ) : (
            <span className="text-[11px] text-ink-faint">nothing waiting</span>
          )}
        </div>

        <p className="text-[11px] text-ink-faint mt-1 flex items-center gap-1.5 flex-wrap">
          {scheduled ? (
            <>
              <Clock size={11} />
              <span>
                {describeIngestWindow({ timeOfDay: project.timeOfDay, timezone: project.timezone, everyMinutes: project.everyMinutes })}{' '}
                {project.timezone}
              </span>
              {/* The instant the worker will actually fire on, computed by the same function the worker
                  uses — so this cannot drift from the schedule it describes. */}
              {project.nextRunAt && <span>· next {new Date(project.nextRunAt).toLocaleString()}</span>}
              {!project.timeOfDay && <span className="text-warning-text">· no time set, so it never fires</span>}
            </>
          ) : (
            <span>Absorbed as it arrives</span>
          )}
          {project.oldest && <span>· oldest arrived {timeAgo(project.oldest)}</span>}
        </p>
      </div>

      {project.segments > 0 && (
        <button
          onClick={onProcess}
          disabled={disabled}
          className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
          Process now
        </button>
      )}
    </div>

    {/* Mounted only while open, which is also what triggers the fetch — the list above is the whole
        tree, and every item carries an excerpt. */}
    {open && project.segments > 0 && (
      <WaitingItems
        projectId={project.projectId}
        version={version}
        busyPool={busyPool}
        disabled={disabled}
        onProcessNext={onProcessNext}
      />
    )}
    </li>
  );
}

/**
 * The individual pieces waiting in one project, grouped into the runs that would take them.
 *
 * The grouping is the server's — the packer's own function against this project's real budget — rather
 * than recomputed here, so "these go together, those wait for a second run" is what will actually
 * happen. Ordered oldest-first for the same reason the batch is: the agent is told to treat older
 * material as history rather than as an update, so the order is the meaning, not a sort preference.
 */
function WaitingItems({
  projectId,
  version,
  busyPool,
  disabled,
  onProcessNext,
}: {
  projectId: string;
  version: number;
  busyPool: 'place' | 'pinned' | null;
  disabled: boolean;
  onProcessNext: (pool: 'place' | 'pinned') => void;
}) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; segments: WaitingSegment[] }
  >({ status: 'loading' });

  useEffect(() => {
    let current = true;
    setState({ status: 'loading' });
    api.kb
      .waitingSegments(projectId)
      .then((page) => { if (current) setState({ status: 'ready', segments: page.segments }); })
      .catch((err: Error) => { if (current) setState({ status: 'error', message: err.message }); });
    return () => { current = false; };
  }, [projectId, version]);

  if (state.status === 'loading') {
    return (
      <p className="mt-2 ml-5 flex items-center gap-2 text-[11px] text-ink-faint">
        <Loader2 size={11} className="animate-spin" />
        Loading what is waiting…
      </p>
    );
  }

  if (state.status === 'error') {
    return <p className="mt-2 ml-5 text-[11px] text-danger-text">Couldn’t load the waiting items: {state.message}</p>;
  }

  if (state.segments.length === 0) {
    // Reachable: the aggregate that produced the count is a moment older than this fetch, so a window
    // that fired in between leaves a row claiming items and a project with none.
    return <p className="mt-2 ml-5 text-[11px] text-ink-faint">Nothing is waiting any more — it has just been absorbed.</p>;
  }

  // Two pools, never mixed into one run: content still to be placed and content pinned here. Shown
  // as two lists for the same reason they are packed as two — a single "next run" heading over both
  // would promise a grouping that cannot happen.
  const pools = [
    { toPlace: true, segments: state.segments.filter((segment) => segment.toPlace) },
    { toPlace: false, segments: state.segments.filter((segment) => !segment.toPlace) },
  ].filter((pool) => pool.segments.length > 0);

  const runCount = pools.reduce((sum, pool) => sum + new Set(pool.segments.map((s) => s.batch)).size, 0);

  return (
    <div className="mt-2 ml-5 space-y-3">
      {pools.map((pool) => {
        const batches = [...new Set(pool.segments.map((segment) => segment.batch))].sort((a, b) => a - b);

        return (
          <div key={String(pool.toPlace)} className="space-y-3">
            {pools.length > 1 && (
              <p className="text-[10px] text-ink-muted leading-snug">
                {pool.toPlace
                  ? 'Arrived without a destination — the ingestor decides where these belong.'
                  : 'Pinned to this project, so these stay here.'}
              </p>
            )}

            {batches.map((batch) => {
              const items = pool.segments.filter((segment) => segment.batch === batch);
              const chars = items.reduce((sum, item) => sum + item.chars, 0);

              return (
                <div key={batch}>
                  <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint mb-1.5">
                    {/* Naming the first group "next run" rather than "batch 1" — the question someone
                        opened this to answer is what happens when the window fires, not how the
                        packer counts. */}
                    {batch === 1 ? 'Next run' : `Then, run ${batch}`}
                    {pool.toPlace && (
                      <span className="inline-flex items-center gap-1 font-normal normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-accent-bg text-accent-text">
                        <FolderTree size={9} />
                        places itself
                      </span>
                    )}
                    <span className="font-normal normal-case tracking-normal">
                      {items.length} item{items.length === 1 ? '' : 's'} · {formatChars(chars)}
                    </span>
                    {/* Only on the next run: later groups go in order once this one has, and taking
                        run 3 first would have the agent read newer material before older. After this
                        dispatches the list refetches and run 2 becomes the next one. */}
                    {batch === 1 && (
                      <button
                        onClick={() => onProcessNext(pool.toPlace ? 'place' : 'pinned')}
                        disabled={disabled}
                        title="Absorb just this run now; the rest keep waiting"
                        className="ml-auto inline-flex items-center gap-1 font-normal normal-case tracking-normal text-[11px] text-ink-muted hover:text-ink transition-colors disabled:opacity-40"
                      >
                        {busyPool === (pool.toPlace ? 'place' : 'pinned') ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <Play size={11} />
                        )}
                        Process now
                      </button>
                    )}
                  </p>

                  <ul className="border border-border rounded-md divide-y divide-border-subtle overflow-hidden">
                    {items.map((item) => (
                      <WaitingItemRow key={item.id} item={item} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        );
      })}

      {runCount > 1 && (
        <p className="text-[10px] text-ink-faint leading-snug">
          More is waiting than fits one pass, so it will be absorbed as several runs, oldest first.
        </p>
      )}
    </div>
  );
}

function WaitingItemRow({ item }: { item: WaitingSegment }) {
  const Icon = SOURCE_ICONS[item.sourceType] ?? FileText;
  const sourceLabels = useSourceLabels();
  const split = item.total > 1;

  return (
    <li className="px-3 py-2 bg-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon size={11} className="text-ink-faint flex-shrink-0" />
        <span className="text-[11px] font-medium text-ink-secondary truncate max-w-[18rem]">
          {formatSource(item.source, sourceLabels)}
        </span>

        {/* One arrival that came in several pieces — a producer that files a day of messages, or a
            row written before splitting moved inside the run. Never a batch boundary: every piece of
            one arrival is absorbed by one run or waits together for the next. */}
        {split && (
          <span
            title="This arrival came in several pieces; they are absorbed together"
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-ink-muted flex-shrink-0"
          >
            <Scissors size={9} />
            piece {item.index} of {item.total}
          </span>
        )}

        <span className="text-[10px] text-ink-faint flex-shrink-0">{formatChars(item.chars)}</span>

        {item.attempts > 0 && (
          <span
            title={`A run took this piece and failed ${item.attempts} time${item.attempts === 1 ? '' : 's'}. Retries are capped, after which it is given up on.`}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-warning-bg text-warning-text flex-shrink-0"
          >
            <AlertTriangle size={9} />
            retry {item.attempts}
          </span>
        )}

        {item.oversized && (
          <span
            title="Larger than one pass on its own, so it gets a run to itself and is split inside it"
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-ink-muted flex-shrink-0"
          >
            split inside the run
          </span>
        )}

        {/* Its own timestamp, not its arrival time — that is what orders the batch and what the agent
            is told to read it as. They differ for anything backfilled from a source. */}
        <span className="ml-auto text-[10px] text-ink-faint whitespace-nowrap flex-shrink-0" title={`Dated ${formatDate(item.sourceTimestamp)} · arrived ${formatDate(item.arrivedAt)}`}>
          {timeAgo(item.sourceTimestamp)}
        </span>
      </div>

      {item.title && (
        <p className="text-[11px] text-ink-muted italic mt-1 truncate" title={item.title}>
          {item.title}
        </p>
      )}

      <p className="text-[11px] text-ink-faint leading-snug mt-1 line-clamp-2">{item.excerpt}</p>
    </li>
  );
}

function formatChars(chars: number): string {
  return chars >= 10_000 ? `${Math.round(chars / 1000)}k chars` : `${chars} chars`;
}
